/**
 * Who is in the room: the student roster and the adults teaching it.
 *
 * Roster and instructors were two separate cards that always sat next to each
 * other and answered the same question, so they share one page.
 */
import { useOutletContext } from 'react-router-dom';
import { Plus, UserPlus, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import ClassHealthRoster from '../ClassHealthRoster';
import type { ClassDetailContext } from '../classDetailTypes';

function ClassPeoplePage() {
  const {
    offering,
    isAdmin,
    activeEntries,
    droppedEntries,
    openAddStudents,
    openAssignInstructor,
    requestDropStudent,
    requestRemoveInstructor,
  } = useOutletContext<ClassDetailContext>();

  const instructors = offering.instructors;
  const capacityText = offering.capacity
    ? `${activeEntries.length}/${offering.capacity}`
    : `${activeEntries.length}`;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Roster ({capacityText} students)</CardTitle>
              <CardDescription>Students enrolled in this class</CardDescription>
            </div>
            {isAdmin && (
              <Button size="sm" onClick={openAddStudents}>
                <Plus className="h-4 w-4 mr-1" />
                Add Students
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {activeEntries.length === 0 && droppedEntries.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-muted-foreground">No students on the roster yet.</p>
              {isAdmin && (
                <Button variant="outline" size="sm" className="mt-2" onClick={openAddStudents}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add your first students
                </Button>
              )}
            </div>
          ) : (
            <ClassHealthRoster
              classOfferingId={offering.id}
              activeEntries={activeEntries}
              droppedEntries={droppedEntries}
              isAdmin={isAdmin}
              onDropStudent={requestDropStudent}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Instructors</CardTitle>
              <CardDescription>Teachers assigned to this class</CardDescription>
            </div>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={openAssignInstructor}>
                <UserPlus className="h-4 w-4 mr-1" />
                Assign Instructor
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {instructors.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-muted-foreground">No instructors assigned yet.</p>
              {isAdmin && (
                <Button variant="outline" size="sm" className="mt-2" onClick={openAssignInstructor}>
                  <UserPlus className="h-4 w-4 mr-1" />
                  Assign first instructor
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {instructors.map((inst) => (
                <div key={inst.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{inst.person.firstName} {inst.person.lastName}</span>
                    <Badge variant="outline">{inst.role}</Badge>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => requestRemoveInstructor(inst)}
                      aria-label={`Remove ${inst.person.firstName} ${inst.person.lastName} as instructor`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ClassPeoplePage;
